import { useState, useEffect, useRef } from 'react';

// Custom hook for slider functionality
const useSlider = ({
  sliderId,
  imageSelector,
  defaultCardsPerView = 3,
  autoSlideDelay = 5000,
  autoSlideEnabled = true,
  paddingOffset = 20,
  breakpoints = {
    mobile: 768,
    tablet: 992,
  },
}) => {
  const sliderRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardsPerView, setCardsPerView] = useState(defaultCardsPerView);
  const [cardWidth, setCardWidth] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState(0);
  const [translate, setTranslate] = useState({ prev: 0, current: 0 });
  const autoSlideIntervalRef = useRef(null);

  // Update cards per view based on screen size
  const updateCardsPerView = () => {
    if (window.innerWidth < breakpoints.mobile) {
      setCardsPerView(1);
    } else if (window.innerWidth < breakpoints.tablet) {
      setCardsPerView(2);
    } else {
      setCardsPerView(3);
    }

    // Update card width with proper margin calculation
    if (sliderRef.current && sliderRef.current.children.length > 0) {
      const cardStyle = window.getComputedStyle(sliderRef.current.children[0]);
      const newCardWidth =
        sliderRef.current.children[0].offsetWidth + parseInt(cardStyle.marginRight || 0);
      setCardWidth(newCardWidth);
      
      // Reset to initial position
      setCurrentIndex(0);
      setTranslate({
        prev: paddingOffset,
        current: paddingOffset
      });
      
      if (sliderRef.current) {
        sliderRef.current.style.transition = 'none';
        sliderRef.current.style.transform = `translateX(${paddingOffset}px)`;
      }
    }
  };

  // Initialize on component mount
  useEffect(() => {
    if (sliderRef.current) {
      // Make images non-draggable
      const images = sliderRef.current.querySelectorAll(imageSelector);

      images.forEach((img) => {
        img.setAttribute('draggable', 'false');
        img.addEventListener('dragstart', (e) => e.preventDefault());
      });

      // Update cards per view based on screen size
      updateCardsPerView();
      
      // Set initial position with padding
      setCurrentIndex(0);
      setTranslate({
        prev: paddingOffset,
        current: paddingOffset
      });
      
      if (sliderRef.current) {
        sliderRef.current.style.transition = 'none';
        sliderRef.current.style.transform = `translateX(${paddingOffset}px)`;
      }

      // Auto-sliding disabled
      // if (autoSlideEnabled) {
      //   startAutoSlide();
      // }

      // Cleanup on unmount
      return () => {
        if (autoSlideIntervalRef.current) {
          clearInterval(autoSlideIntervalRef.current);
        }
      };
    }
  }, []);

  // Add resize event listener
  useEffect(() => {
    window.addEventListener('resize', updateCardsPerView);
    return () => {
      window.removeEventListener('resize', updateCardsPerView);
    };
  }, []);

  // Update slider position when currentIndex changes
  useEffect(() => {
    slideTo(currentIndex);
  }, [currentIndex, cardWidth]);

  const slideTo = (index) => {
    if (!sliderRef.current) return;

    // Calculate max index
    const maxIndex = Math.max(0, sliderRef.current.children.length - cardsPerView);

    // Ensure index is within bounds
    if (index < 0) index = 0;
    if (index > maxIndex) index = maxIndex;

    setCurrentIndex(index);

    // Add padding offset to prevent cards from being cut off
    // For the first card, add extra padding to position it at the beginning of the page
    const newTranslate = index === 0 ? paddingOffset : index * -cardWidth + paddingOffset;

    setTranslate({
      prev: newTranslate,
      current: newTranslate,
    });

    // Apply smoother transition with linear easing
    if (sliderRef.current) {
      sliderRef.current.style.transition = 'transform 0.4s ease-out';
      sliderRef.current.style.transform = `translateX(${newTranslate}px)`;
    }
  };

  const slideToPrev = () => {
    if (!sliderRef.current) return;

    const maxIndex = sliderRef.current.children.length - cardsPerView;

    // Don't loop to the end, just stop at the beginning
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }

    pauseAutoSlide();
  };

  const slideToNext = () => {
    if (!sliderRef.current) return;

    const maxIndex = sliderRef.current.children.length - cardsPerView;

    // Don't loop to the beginning, just stop at the end
    if (currentIndex < maxIndex) {
      setCurrentIndex(currentIndex + 1);
    }

    pauseAutoSlide();
  };

  // Commented out to prevent auto-rotation of sliders
  const startAutoSlide = () => {
    // Auto-sliding functionality disabled
    // Clear any existing interval
    if (autoSlideIntervalRef.current) {
      clearInterval(autoSlideIntervalRef.current);
    }

    // Auto-rotation disabled
    // autoSlideIntervalRef.current = setInterval(() => {
    //   slideToNext();
    // }, autoSlideDelay);
  };

  // Commented out to prevent auto-rotation of sliders
  const pauseAutoSlide = () => {
    if (autoSlideIntervalRef.current) {
      clearInterval(autoSlideIntervalRef.current);

      // Auto-restart disabled
      // setTimeout(() => {
      //   if (autoSlideEnabled) {
      //     startAutoSlide();
      //   }
      // }, 10000);
    }
  };

  const getPositionX = (event) => {
    return event.type.includes('mouse') ? event.pageX : event.touches[0].clientX;
  };

  const touchStart = (event) => {
    // Get starting position
    const posX = getPositionX(event);
    setStartPos(posX);
    setIsDragging(true);

    // Remove transition during drag
    if (sliderRef.current) {
      sliderRef.current.style.transition = 'none';
      sliderRef.current.style.cursor = 'grabbing';
    }

    // Store the current translate value
    if (sliderRef.current) {
      const transform = window.getComputedStyle(sliderRef.current).getPropertyValue('transform');
      if (transform && transform !== 'none') {
        const matrix = new DOMMatrixReadOnly(transform);
        const currentTranslate = matrix.m41; // Get the X translation value
        setTranslate((prev) => ({ ...prev, prev: currentTranslate, current: currentTranslate }));
      }
    }
  };

  const touchMove = (event) => {
    if (isDragging) {
      const currentPosition = getPositionX(event);
      const diff = currentPosition - startPos;

      // Calculate new position with 1:1 movement (no resistance)
      const newTranslate = translate.prev + diff;
      
      // Add some resistance at the edges to make it feel more natural
      const maxIndex = Math.max(0, sliderRef.current.children.length - cardsPerView);
      const minTranslate = paddingOffset;
      const maxTranslate = maxIndex * -cardWidth + paddingOffset;
      
      let finalTranslate = newTranslate;
      
      // Add resistance when trying to drag beyond the first card
      if (newTranslate > minTranslate + 50) {
        finalTranslate = minTranslate + (newTranslate - minTranslate) * 0.2;
      }
      
      // Add resistance when trying to drag beyond the last card
      if (newTranslate < maxTranslate - 50) {
        finalTranslate = maxTranslate - (maxTranslate - newTranslate) * 0.2;
      }
      
      setTranslate((prev) => ({
        ...prev,
        current: finalTranslate,
      }));

      // Apply transform without transition for immediate response
      if (sliderRef.current) {
        sliderRef.current.style.transform = `translateX(${finalTranslate}px)`;
      }
    }
  };

  const touchEnd = () => {
    if (!isDragging) return;

    setIsDragging(false);

    // Calculate how much the slider was dragged
    const movedBy = translate.current - translate.prev;

    // Use a lower threshold for easier sliding (15% of card width)
    if (Math.abs(movedBy) > Math.min(80, cardWidth * 0.15)) {
      if (movedBy > 0) {
        // Calculate the exact index to move to instead of using slideToPrev
        const newIndex = Math.max(0, currentIndex - 1);
        setCurrentIndex(newIndex);
      } else {
        // Calculate the exact index to move to instead of using slideToNext
        const maxIndex = Math.max(0, sliderRef.current.children.length - cardsPerView);
        const newIndex = Math.min(maxIndex, currentIndex + 1);
        setCurrentIndex(newIndex);
      }
    } else {
      // Otherwise snap back to current card with smoother animation
      if (sliderRef.current) {
        sliderRef.current.style.transition = 'transform 0.3s ease-out';
        sliderRef.current.style.transform = `translateX(${translate.prev}px)`;
        
        // Update the current translate to match
        setTranslate(prev => ({
          ...prev,
          current: prev.prev
        }));
      }
    }

    // Reset cursor
    if (sliderRef.current) {
      sliderRef.current.style.cursor = 'grab';
    }
  };

  const handleTransitionEnd = () => {
    // Remove transition after animation completes
    if (!isDragging && sliderRef.current) {
      sliderRef.current.style.transition = 'none';
    }
  };

  return {
    sliderRef,
    currentIndex,
    slideToPrev,
    slideToNext,
    touchStart,
    touchMove,
    touchEnd,
    handleTransitionEnd,
  };
};

export default useSlider;
